import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, X, Loader2, Trash2, ShieldCheck, User } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";

export default function Team() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "hr_member" });
  const [saving, setSaving] = useState(false);
  const isAdmin = user?.role === "tenant_admin";

  const load = async () => {
    try { const { data } = await api.get("/team"); setMembers(data); }
    catch { toast.error("Failed to load team"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/team", form);
      toast.success("Member added");
      setShowModal(false); setForm({ name: "", email: "", password: "", role: "hr_member" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove ${name} from the team?`)) return;
    try { await api.delete(`/team/${id}`); toast.success("Member removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AppLayout>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tighter">Team</h1>
          <p className="text-[#86868B] mt-1">HR managers and executives with dashboard access.</p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowModal(true)} data-testid="add-member-btn"
            className="bg-[#007AFF] text-white rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#0051FF] active:scale-95 transition-all flex items-center gap-2">
            <Plus size={18} /> Add Member
          </button>
        )}
      </div>

      {loading ? <div className="text-[#86868B]">Loading…</div> : (
        <div className="flex flex-col gap-3">
          {members.map((m, i) => (
            <motion.div key={m.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              data-testid="member-row"
              className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-black/5 p-4 flex items-center gap-4">
              <div className="w-11 h-11 rounded-full bg-[#1D1D1F] text-white flex items-center justify-center font-bold text-sm">
                {m.name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold tracking-tight truncate">{m.name} {m.id === user.id && <span className="text-xs font-medium text-[#86868B]">(You)</span>}</h3>
                <p className="text-sm text-[#86868B] truncate">{m.email}</p>
              </div>
              <span className={`text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 ${m.role === "tenant_admin" ? "bg-[#007AFF]/10 text-[#007AFF]" : "bg-gray-100 text-[#86868B]"}`}>
                {m.role === "tenant_admin" ? <ShieldCheck size={13} /> : <User size={13} />}
                {m.role === "tenant_admin" ? "Admin" : "HR Member"}
              </span>
              {isAdmin && m.id !== user.id && (
                <button onClick={() => remove(m.id, m.name)} data-testid="remove-member-btn"
                  className="w-9 h-9 rounded-full bg-gray-100 hover:bg-[#FF3B30]/10 text-[#86868B] hover:text-[#FF3B30] flex items-center justify-center transition-colors"><Trash2 size={15} /></button>
              )}
            </motion.div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-5" onClick={() => setShowModal(false)}>
          <motion.form initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()} onSubmit={add}
            className="bg-white rounded-3xl shadow-xl border border-black/5 p-7 w-full max-w-md" data-testid="member-modal">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-extrabold tracking-tight">Add Team Member</h2>
              <button type="button" onClick={() => setShowModal(false)} className="text-[#86868B] hover:text-[#1D1D1F]"><X size={20} /></button>
            </div>
            <div className="space-y-3.5">
              <Fld label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="member-name" />
              <Fld label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="member-email" />
              <Fld label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testId="member-password" />
              <div>
                <label className="text-sm font-semibold">Role</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="member-role"
                  className="mt-1 w-full bg-gray-50/70 border border-black/10 rounded-xl px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#007AFF]/30 outline-none transition-colors">
                  <option value="hr_member">HR Member</option>
                  <option value="tenant_admin">Admin</option>
                </select>
              </div>
            </div>
            <button type="submit" disabled={saving} data-testid="save-member-submit"
              className="mt-6 w-full bg-[#007AFF] text-white rounded-full px-6 py-3 font-semibold text-sm hover:bg-[#0051FF] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 size={16} className="animate-spin" />} Add member
            </button>
          </motion.form>
        </div>
      )}
    </AppLayout>
  );
}

function Fld({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="text-sm font-semibold">{label}</label>
      <input type={type} required value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="mt-1 w-full bg-gray-50/70 border border-black/10 rounded-xl px-4 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#007AFF]/30 focus:border-[#007AFF] outline-none transition-colors" />
    </div>
  );
}
